import React from 'react';
import {
  Dialog,
  Box,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { MaintenanceRecord, MaintenanceType, MaintenanceStatus, MaintenancePlan } from '../../types/maintenance';
import { useAuth } from '../../contexts/AuthContext';
import DocumentUpload from './DocumentUpload';
import MaintenanceForm from '../maintenance/MaintenanceForm';
import { MaintenancePlanForm } from '../maintenance/MaintenancePlanForm';
import VehicleAssignment from './VehicleAssignment';
import MileageEntry from './MileageEntry';
import { FuelReceiptEntry } from '../fuel/FuelReceiptEntry';
import { createMaintenanceRecord, updateMaintenanceRecord } from '../../services/maintenanceService';

interface VehicleDialogsProps {
  vehicle: Vehicle;
  showUploadDialog: boolean;
  showAssignDialog: boolean;
  showMileageDialog: boolean;
  showMaintenanceForm: boolean;
  showMaintenancePlanForm: boolean;
  showFuelEntryDialog: boolean;
  selectedMaintenance?: MaintenanceRecord;
  loading: boolean;
  onCloseUpload: () => void;
  onCloseAssign: () => void;
  onCloseMileage: () => void;
  onCloseMaintenanceForm: () => void;
  onClosePlanForm: () => void;
  onCloseFuelEntry: () => void;
  onUpdate: () => void;
  onUploadComplete: () => void;
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }) => void;
}

const VehicleDialogs: React.FC<VehicleDialogsProps> = ({
  vehicle,
  showUploadDialog,
  showAssignDialog,
  showMileageDialog,
  showMaintenanceForm,
  showMaintenancePlanForm,
  showFuelEntryDialog,
  selectedMaintenance,
  loading,
  onCloseUpload,
  onCloseAssign,
  onCloseMileage,
  onCloseMaintenanceForm,
  onClosePlanForm,
  onCloseFuelEntry,
  onUpdate,
  onUploadComplete,
  setSnackbar
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <>
      {/* Maintenance Form Dialog */}
      <Dialog 
        open={showMaintenanceForm} 
        onClose={onCloseMaintenanceForm}
        maxWidth="md"
        fullWidth
      >
        <MaintenanceForm
          maintenance={selectedMaintenance}
          onSubmit={async (formData) => {
            try {
              if (selectedMaintenance) {
                await updateMaintenanceRecord(selectedMaintenance.id, {
                  ...formData,
                  updatedBy: user?.id || '',
                  updatedAt: Timestamp.now(),
                });
                onCloseMaintenanceForm();
                onUpdate();
                setSnackbar({
                  open: true,
                  message: t('maintenance.form.messages.updateSuccess'),
                  severity: 'success'
                });
              } else {
                try {
                  if (!formData.type || !formData.status || !formData.description) {
                    throw new Error(t('maintenance.form.errors.missingRequiredFields'));
                  }

                  const type = formData.type as MaintenanceType;
                  const status = formData.status as MaintenanceStatus;

                  if (!['preventive', 'corrective'].includes(type)) {
                    throw new Error(t('maintenance.form.errors.invalidType'));
                  }

                  if (!['pending', 'in_progress', 'completed', 'cancelled'].includes(status)) {
                    throw new Error(t('maintenance.form.errors.invalidStatus'));
                  }

                  const newRecord: Omit<MaintenanceRecord, 'id'> = {
                    vehicleId: vehicle.id,
                    organizationId: vehicle.organizationId,
                    type,
                    status,
                    description: formData.description || '',
                    costHT: typeof formData.costHT === 'number' ? formData.costHT : 0,
                    costTTC: typeof formData.costTTC === 'number' ? formData.costTTC : 0,
                    openDate: formData.openDate || Timestamp.now(),
                    closeDate: formData.status === 'completed' ? (formData.closeDate || Timestamp.now()) : undefined,
                    serviceProviderId: formData.serviceProviderId,
                    events: [],
                    documents: [],
                    detailedOperations: formData.detailedOperations || undefined,
                    createdBy: user?.id || '',
                    updatedBy: user?.id || '',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                  };
                  await createMaintenanceRecord(newRecord);
                  onCloseMaintenanceForm();
                  onUpdate();
                  setSnackbar({
                    open: true,
                    message: t('maintenance.form.messages.createSuccess'),
                    severity: 'success'
                  });
                } catch (error) {
                  console.error('Error creating maintenance record:', error);
                  setSnackbar({
                    open: true,
                    message: error instanceof Error ? error.message : t('maintenance.form.errors.unknownError'),
                    severity: 'error'
                  });
                }
              }
            } catch (error) {
              console.error('Error saving maintenance record:', error);
              setSnackbar({
                open: true,
                message: error instanceof Error ? error.message : t('maintenance.form.errors.unknownError'),
                severity: 'error'
              });
            }
          }}
          onCancel={onCloseMaintenanceForm}
          vehicles={[{ id: vehicle.id, name: `${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})` }]}
          serviceProviders={[]} // TODO: Add service providers list
          preSelectedVehicleId={vehicle.id}
          disabled={false}
        />
      </Dialog>

      {/* Maintenance Plan Form Dialog */}
      <Dialog
        open={showMaintenancePlanForm}
        onClose={onClosePlanForm}
        maxWidth="md"
        fullWidth
      >
        <MaintenancePlanForm
          vehicleId={vehicle.id}
          organizationId={vehicle.organizationId}
          onSubmit={() => {
            onClosePlanForm();
            onUpdate();
          }}
          onCancel={onClosePlanForm}
        />
      </Dialog>

      {/* Document Upload Dialog */}
      <DocumentUpload
        vehicleId={vehicle.id}
        organizationId={vehicle.organizationId}
        open={showUploadDialog}
        onClose={onCloseUpload}
        onUploadComplete={onUploadComplete}
      />

      {/* Vehicle Assignment Dialog */}
      <VehicleAssignment
        vehicle={vehicle}
        open={showAssignDialog}
        onClose={onCloseAssign}
        onAssign={onUpdate}
      />

      {/* Mileage Entry Dialog */}
      <MileageEntry
        vehicleId={vehicle.id}
        open={showMileageDialog}
        onClose={onCloseMileage}
        onSubmit={onUpdate}
        currentMileage={vehicle.currentMileage}
      />

      {/* Fuel Receipt Entry Dialog */}
      {showFuelEntryDialog && (
        <Dialog
          open={showFuelEntryDialog}
          onClose={onCloseFuelEntry}
          maxWidth="md"
          fullWidth
        >
          <FuelReceiptEntry
            vehicle={vehicle}
            organizationId={vehicle.organizationId}
            onSuccess={() => {
              onCloseFuelEntry();
              onUpdate();
            }}
            onCancel={onCloseFuelEntry}
          />
        </Dialog>
      )}
    </>
  );
};

export default VehicleDialogs;
